import { coerceBooleanProperty } from "@angular/cdk/coercion";
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { Icon } from "pax-ui/icons";

import { Spinner } from "../spinner";

@Component({
  standalone: true,
  imports: [Icon, Spinner, CommonModule],
  // We use the native <button> tag - in order to select it we use an attribute.
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: "button[paxButton], a[paxButton]",
  templateUrl: "button.html",
  styleUrls: ["button.scss"],
  exportAs: "paxButton",
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Button implements OnInit {
  @HostBinding("attr.disabled")
  get isDisabled() {
    const shouldDisableButton = this.disabled || this.isBusy;
    return shouldDisableButton || REMOVE_ATTRIBUTE_FROM_DOM;
  }

  @Input() variant: ThemeColor = "primary";

  @Input() size: Size = "md";

  @Input({ transform: coerceBooleanProperty })
  disabled = false;

  /**
   * Must be a valid icon name from the pax-ui/icon library
   * Important: Make sure icon is registered in IconRegistry in parent component.
   *
   * Usage: e.g. iconLeft="attention"
   */
  @Input() iconLeft: string | undefined;
  @Input() iconRight: string | undefined;

  /**
   * Disable button and add loading spinner
   */
  @Input() isBusy: boolean | undefined;

  classList: DOMTokenList;
  el: HTMLButtonElement;

  constructor(elementRef: ElementRef<HTMLButtonElement>) {
    this.el = elementRef.nativeElement;
    this.classList = this.el.classList;
    this.classList.add("button");
  }

  ngOnInit() {
    if (this.iconLeft) {
      this.classList.add("button--icon-left");
    }
    if (this.iconRight) {
      this.classList.add("button--icon-right");
    }

    this.classList.add(`button-${this.variant}`);
    this.classList.add(`button-${this.size}`);
  }
}

/**
 * We have to explicitly return 'null' or 'undefined' to remove the attribute from the DOM.
 */
const REMOVE_ATTRIBUTE_FROM_DOM = null;

export type ThemeColor =
  | "primary"
  | "primary-outlined"
  | "ghost"
  | "prune-outlined"
  | "prune"
  | "link"
  | "white-outlined"
  | "basic"
  | "basic-prune";

export type Size = "sm" | "md" | "lg";
